import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Megaphone, X } from 'lucide-react'
import { fetchPublicMessages, isLive, isOrgAlias, readDismissed, writeDismissed, type PublicMessage } from './publicMessages'

/**
 * An operator's current public announcements (logbook decision 20), as a
 * dismissible banner under the page's header. The shell's, not a module's:
 * pages put it below their own header.
 *
 * - `operator`: the org alias. Nothing renders without one.
 * - `zone`: narrow to announcements for the whole operator or this zone.
 *
 * The newest shows; the rest fold under "N more". Each can be dismissed, and
 * stays dismissed in this browser.
 */
export function Announcements({
  operator,
  zone,
}: {
  operator: string | null | undefined
  zone?: string | null
}) {
  const enabled = isOrgAlias(operator)
  const { data: messages = [] } = useQuery({
    queryKey: ['public-messages', operator, zone ?? null],
    queryFn: ({ signal }) => fetchPublicMessages(operator!, zone, signal),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  const [dismissed, setDismissed] = useState<string[]>(readDismissed)
  const [expanded, setExpanded] = useState(false)

  const shown = useMemo(() => {
    const hidden = new Set(dismissed)
    return messages.filter((m) => isLive(m) && !hidden.has(m.id))
  }, [messages, dismissed])

  if (!enabled || shown.length === 0) return null

  const dismiss = (id: string) => {
    // Re-read first: another tab may have dismissed others meanwhile.
    const next = [...readDismissed().filter((d) => d !== id), ...dismissed.filter((d) => d !== id), id]
    const unique = [...new Set(next)]
    setDismissed(unique)
    writeDismissed(unique)
  }

  const [first, ...rest] = shown
  const visible = expanded ? shown : [first]

  return (
    <section
      aria-label="Announcements"
      className="shrink-0 border-b border-border bg-primary/5 px-4 py-2 space-y-2"
    >
      {visible.map((message) => (
        <AnnouncementRow key={message.id} message={message} onDismiss={() => dismiss(message.id)} />
      ))}
      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          className="ml-7 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3.5 h-3.5" /> Show fewer
            </>
          ) : (
            <>
              <ChevronDown className="w-3.5 h-3.5" /> {rest.length} more announcement{rest.length === 1 ? '' : 's'}
            </>
          )}
        </button>
      )}
    </section>
  )
}

function AnnouncementRow({ message, onDismiss }: { message: PublicMessage; onDismiss: () => void }) {
  const until = formatUntil(message.endsAt)
  return (
    <div className="flex items-start gap-3 min-w-0">
      <Megaphone className="w-4 h-4 mt-0.5 shrink-0 text-primary" aria-hidden />
      <div className="flex-1 min-w-0 text-sm">
        <p className="font-semibold break-words">{message.subject}</p>
        {message.body && (
          <p className="text-muted-foreground whitespace-pre-line break-words">{message.body}</p>
        )}
        {until && <p className="text-xs text-muted-foreground mt-0.5">{until}</p>}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={`Dismiss announcement: ${message.subject}`}
        title="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

function formatUntil(endsAt: string | null): string | null {
  if (!endsAt) return null
  const date = new Date(endsAt)
  if (Number.isNaN(date.getTime())) return null
  return `Until ${date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
}
